import React from 'react';
import { motion } from 'framer-motion';
import { FaMoon, FaPhoneSlash, FaClock, FaDollarSign } from 'react-icons/fa';

const problems = [
  {
    id: "after-hours",
    icon: <FaMoon className="text-5xl text-emerald-400 mb-4" />,
    title: "The After-Hours Ghost",
    stats: "42% of PI leads happen after 5 PM.",
    description: "When your phone goes to voicemail, they call the next lawyer on Google. By morning, that $50,000 case belongs to someone else."
  },
  {
    id: "missed-calls",
    icon: <FaPhoneSlash className="text-5xl text-emerald-400 mb-4" />,
    title: "The Missed Call Massacre",
    stats: "35% of law firm calls go unanswered.",
    description: "That's not a 'phone issue' - that's a $144,000/year revenue leak. Every unanswered call is a case you'll never see."
  },
  {
    id: "speed-to-lead",
    icon: <FaClock className="text-5xl text-emerald-400 mb-4" />,
    title: "The Speed-to-Lead Gap",
    stats: "78% of clients hire the FIRST lawyer who responds.",
    description: "If you call back in 2 hours, you've already lost. The firm that responds in 2 minutes wins the case."
  },
  {
    id: "employee-cost",
    icon: <FaDollarSign className="text-5xl text-emerald-400 mb-4" />,
    title: "The $4,000/Month Employee Problem",
    stats: "A full-time intake person costs $4,000/month + benefits.",
    description: "They still can't work at 2 AM. They still take sick days. They still miss calls when the other line rings."
  }
];

const ProblemSection = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50" id="problems">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Sound <span className="gradient-text">Familiar?</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Every day, PI firms lose high-value cases to these preventable problems
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover-lift"
              data-testid={`problem-card-${index}`}
            >
              <div className="flex flex-col items-start">
                {problem.icon}
                <h3 className="text-2xl font-bold mb-3 text-white">{problem.title}</h3>
                <p className="text-emerald-400 font-semibold mb-4 text-lg">{problem.stats}</p>
                <p className="text-gray-300 leading-relaxed">{problem.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold mb-4 text-white">
              Ready to Stop the Leak?
            </h3>
            <p className="text-xl text-gray-300 mb-6">
              One missed call could be a $50,000 case walking away. Let's fix that.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
